import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, LayoutDashboard, Settings } from "lucide-react";
import CheckELogo from "@/components/CheckELogo";
import { useAuth } from "@/App";

/**
 * MarketingNav — sticky top nav used on all public/marketing pages.
 *
 * Wherever a visitor can land before authenticating — landing, /experts,
 * /enterprise, /get-verified, /verified/:slug, /login etc — render this
 * so the chrome stays identical and the site feels like one product.
 *
 * Anchor-style links (#how) only work on the landing page. Off-landing,
 * they navigate to "/" then jump to the anchor.
 */
// Mirrors the canonical nav in `_design-system/pages/*.html`: three doorways,
// one per primary audience. Browse-experts and use-cases are still reachable
// via in-page CTAs and the footer — they don't earn a nav slot of their own.
const LINKS = [
  { label: "Builders",          to: "/builders" },
  { label: "Become an expert",  to: "/become-expert" },
  { label: "For teams",         to: "/enterprise" },
];

export default function MarketingNav() {
  const navigate    = useNavigate();
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the user menu when clicking outside it.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [menuOpen]);

  const goAnchor = (anchor) => {
    if (pathname === "/") {
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/${anchor}`);
    }
  };

  // Two-letter initials for the avatar fallback.
  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]+/)
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header
      className={[
        "sticky top-0 z-40 bg-canvas/85 backdrop-blur transition-all",
        scrolled ? "border-b border-slateline-200" : "border-b border-transparent",
      ].join(" ")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center" aria-label="Xpert home">
          <CheckELogo size={32} withWordmark />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          {LINKS.map((l) =>
            l.to ? (
              <Link
                key={l.label}
                to={l.to}
                className="text-sm font-medium text-slateline-600 hover:text-ink transition-colors"
              >
                {l.label}
              </Link>
            ) : (
              <button
                key={l.label}
                onClick={() => goAnchor(l.anchor)}
                className="text-sm font-medium text-slateline-600 hover:text-ink transition-colors"
              >
                {l.label}
              </button>
            ),
          )}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            // Authed: avatar + dropdown
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex h-9 items-center gap-2 pl-1 pr-3 rounded-full
                           border border-slateline-200 hover:border-ink/40 bg-white transition-colors"
                aria-haspopup="menu" aria-expanded={menuOpen}
              >
                <span className="w-7 h-7 rounded-full bg-ink text-white font-semibold text-[11px]
                                 flex items-center justify-center">
                  {initials}
                </span>
                <span className="text-sm font-medium text-ink max-w-[140px] truncate">
                  {user.name || user.email}
                </span>
                <svg width="10" height="10" viewBox="0 0 10 10" className="text-slateline-500">
                  <path d="M1 3l4 4 4-4" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              {menuOpen && (
                <div role="menu"
                     className="absolute right-0 mt-2 w-56 rounded-lg bg-white border border-slateline-200
                                shadow-lg overflow-hidden z-50">
                  <div className="px-4 py-3 border-b border-slateline-200">
                    <div className="text-sm font-semibold text-ink truncate">
                      {user.name || "Signed in"}
                    </div>
                    <div className="text-xs text-slateline-500 truncate">{user.email}</div>
                  </div>
                  <button
                    onClick={() => { setMenuOpen(false); navigate(user.is_expert ? "/expert-dashboard" : "/dashboard"); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink hover:bg-canvas text-left"
                  >
                    <LayoutDashboard className="w-4 h-4 text-slateline-500" />
                    Dashboard
                  </button>
                  {user.is_expert && (
                    <button
                      onClick={() => { setMenuOpen(false); navigate("/expert/edit-profile"); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink hover:bg-canvas text-left"
                    >
                      <Settings className="w-4 h-4 text-slateline-500" />
                      Edit expert profile
                    </button>
                  )}
                  <button
                    onClick={() => { setMenuOpen(false); logout(); navigate("/"); }}
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-ink hover:bg-canvas text-left border-t border-slateline-200"
                  >
                    <LogOut className="w-4 h-4 text-slateline-500" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Anonymous: sign in + primary CTA
            <button
              onClick={() => navigate("/login")}
              className="hidden sm:inline-flex h-9 items-center px-3 text-sm font-medium text-slateline-700 hover:text-ink"
            >
              Sign in
            </button>
          )}

          <button
            onClick={() => navigate("/get-verified")}
            className="inline-flex h-9 items-center gap-1.5 px-4 rounded-md bg-brand-green hover:bg-brand-greenHover text-white text-sm font-semibold shadow-sm transition-colors"
          >
            Submit AI output
            <span aria-hidden="true">→</span>
          </button>
        </div>
      </div>
    </header>
  );
}
